import React, { useState } from 'react';
import './ReportsGrid.css';
import './CommonStyles.css';
import ProfitLossReport from './ProfitLossReport';
import PurchaseSaleReport from './PurchaseSaleReport';
import TaxReport from './TaxReport';
import StockReport from './StockReport';
import StockAdjustmentReport from './StockAdjustmentReport';
import TrendingProductsReport from './TrendingProductsReport';
import ProductPurchaseReport from './ProductPurchaseReport';
import ProductSellReport from './ProductSellReport';
import PurchasePaymentReport from './PurchasePaymentReport';
import SellPaymentReport from './SellPaymentReport';
import SalesRepresentativeReport from './SalesRepresentativeReport';
import ServiceStaffReport from './ServiceStaffReport';
import ActivityLog from './ActivityLog';
import RegisterReport from './RegisterReport';
import MsaTobaccoReport from './MsaTobaccoReport';

const REPORT_CATEGORIES = [
  {
    id: 'financial',
    title: 'Financial Performance',
    icon: '📊',
    reports: [
      { 
        id: 'report-profit-loss', 
        label: 'Profit / Loss Report', 
        icon: '📈', 
        description: 'Track comprehensive revenue, expenses, and net profit.',
        component: ProfitLossReport
      },
      { 
        id: 'report-tax', 
        label: 'Tax Report', 
        icon: '📄', 
        description: 'Overview of all input & output taxes collected.',
        component: TaxReport
      },
      { 
        id: 'expense', 
        label: 'Expense Report', 
        icon: '📋', 
        description: 'Categorized business expenditures over time.',
        component: null
      },
      { 
        id: 'register', 
        label: 'Register Report', 
        icon: '🖥️', 
        description: 'Cash register operations and closure summaries.',
        component: RegisterReport
      },
    ]
  },
  {
    id: 'inventory',
    title: 'Inventory & Stock',
    icon: '📦',
    reports: [
      { 
        id: 'report-stock', 
        label: 'Stock Report', 
        icon: '📊', 
        description: 'Current inventory overview, values, and alerts.',
        component: StockReport
      },
      { 
        id: 'stock-adjustment', 
        label: 'Stock Adjustment', 
        icon: '⚙️', 
        description: 'Log of all manual adjustments and lost items.',
        component: StockAdjustmentReport
      },
      { 
        id: 'trending-products', 
        label: 'Trending Products', 
        icon: '🔥', 
        description: 'Most popular and high-velocity items.',
        component: TrendingProductsReport
      },
      { 
        id: 'items-report', 
        label: 'Items Report', 
        icon: '📝', 
        description: 'Detailed tracking of individual SKUs.',
        component: null
      },
    ]
  },
  {
    id: 'sales',
    title: 'Sales & Purchases',
    icon: '🛒',
    reports: [
      { 
        id: 'report-purchase-sale', 
        label: 'Purchase & Sale', 
        icon: '🔄', 
        description: 'High-level purchase vs. sale comparison.',
        component: PurchaseSaleReport
      },
      { 
        id: 'product-purchase', 
        label: 'Product Purchase', 
        icon: '🛒', 
        description: 'Purchase velocity by product.',
        component: ProductPurchaseReport
      },
      { 
        id: 'product-sell', 
        label: 'Product Sell', 
        icon: '🏷️', 
        description: 'Sales velocity by product.',
        component: ProductSellReport
      },
      { 
        id: 'purchase-payment', 
        label: 'Purchase Payment', 
        icon: '💰', 
        description: 'Outgoing payments against purchase orders.',
        component: PurchasePaymentReport
      },
      { 
        id: 'sell-payment', 
        label: 'Sell Payment', 
        icon: '💳', 
        description: 'Incoming payments from customers.',
        component: SellPaymentReport
      },
    ]
  },
  {
    id: 'staff',
    title: 'Staff & Contacts',
    icon: '👥',
    reports: [
      { 
        id: 'report-supplier-customer', 
        label: 'Supplier & Customer', 
        icon: '📇', 
        description: 'Ledger of all contact interactions & balances.',
        component: null
      },
      { 
        id: 'report-customer-groups', 
        label: 'Customer Groups', 
        icon: '👥', 
        description: 'Sales breakdown by distinct customer groups.',
        component: null
      },
      { 
        id: 'sales-representative', 
        label: 'Sales Representative', 
        icon: '👤', 
        description: 'Performance and commission data by agent.',
        component: SalesRepresentativeReport
      },
      { 
        id: 'service-staff', 
        label: 'Service Staff', 
        icon: '👨‍💼', 
        description: 'Utilization and task completion of service staff.',
        component: ServiceStaffReport
      },
      { 
        id: 'activity-log', 
        label: 'Activity Log', 
        icon: '🕐', 
        description: 'System-wide audit trail of all staff actions.',
        component: ActivityLog
      },
      { 
        id: 'msa-reporting', 
        label: 'MSA Reporting', 
        icon: '📊', 
        description: 'Tobacco and regulatory compliance logs.',
        component: MsaTobaccoReport
      },
    ]
  }
];

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleReportClick = (report) => {
    setSelectedReport(report);
  };

  const handleBackToGrid = () => {
    setSelectedReport(null);
  };

  const filteredCategories = REPORT_CATEGORIES.map(category => ({
    ...category,
    reports: category.reports.filter(report =>
      report.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.reports.length > 0);

  if (selectedReport) {
    const ReportComponent = selectedReport.component;
    return (
      <div className="active-report-container">
        <div className="active-report-topbar">
          <button className="back-to-reports-btn" onClick={handleBackToGrid}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Reports
          </button>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>
            {selectedReport.icon} {selectedReport.label}
          </h2>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {ReportComponent ? <ReportComponent /> : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{selectedReport.icon}</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                {selectedReport.label}
              </h3>
              <p>{selectedReport.description}</p>
              <p style={{ marginTop: '1rem', fontStyle: 'italic' }}>This report is coming soon.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="reports-hub-wrapper">
      <div className="reports-hub-header">
        <h1 className="reports-hub-title">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3v18h18"/>
            <path d="M18 17V9"/>
            <path d="M13 17V5"/>
            <path d="M8 17v-3"/>
          </svg>
          Reports & Analytics
        </h1>
        <p className="reports-hub-desc">
          Select a report to view insights, performance, and detailed financial audits.
        </p>
      </div>

      <div className="reports-hub-search-container">
        <div className="reports-hub-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredCategories.map(category => (
        <div key={category.id} className="reports-category-section">
          <h2 className="reports-category-title">
            <span style={{ fontSize: '1.3rem' }}>{category.icon}</span>
            {category.title}
          </h2>
          <div className="reports-grid">
            {category.reports.map(report => (
              <div
                key={report.id}
                className="report-card"
                onClick={() => handleReportClick(report)}
              >
                <div className="report-card-icon">
                  <span style={{ fontSize: '1.5rem' }}>{report.icon}</span>
                </div>
                <div className="report-card-content">
                  <h3 className="report-card-title">{report.label}</h3>
                  <p className="report-card-desc">{report.description}</p>
                </div>
                <div className="report-card-arrow">→</div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {filteredCategories.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
          <p style={{ fontSize: '1.1rem', fontWeight: '500' }}>No reports found matching "{searchTerm}"</p>
        </div>
      )}
    </div>
  );
};

export default Reports;
